import React, { useState } from 'react';
import uniqueId from 'lodash/uniqueId';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import WaeButton, { BUTTON_VARIANT } from '../../../components/Button';
import WaeStandardTextField from '../../../components/WaeStandardTextField/WaeStandardTextField';
import { BLACK, PRIMARY_PURPLE, RED } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import SectionCard from '../../positionTemplate/components/SectionCard';
import { textFieldSx } from '../components/formFieldStyles';
import RequiredMark from '../components/RequiredMark';
import TimeField from '../components/TimeField';
import {
  DEFAULT_SHIFT_END,
  DEFAULT_SHIFT_START,
  getSectionAnchorId,
  SECTION_IDS,
  SHIFT_NAME_MAX_LENGTH,
} from '../constants';
import { getShiftErrors } from '../helpers/shiftValidation';

const GRID_COLUMNS = 'minmax(200px, 2fr) 160px 160px 40px';

export const createShift = () => ({
  id: uniqueId('shift-'),
  name: '',
  start: DEFAULT_SHIFT_START,
  end: DEFAULT_SHIFT_END,
});

const nameFieldSx = {
  ...textFieldSx,
  '& .MuiInputBase-input': { fontFamily: PRIMARY_FONT[700], fontSize: '14px' },
};

const nameErrorSx = {
  '& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
    { borderColor: RED[50] },
};

const columnLabelSx = {
  color: BLACK[60],
  fontFamily: PRIMARY_FONT[700],
  fontSize: '11px',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const shiftShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  start: PropTypes.number,
  end: PropTypes.number,
});

const ShiftCountChip = ({ count }) => (
  <Box
    component="span"
    data-testid="shifts-count"
    sx={{
      backgroundColor: BLACK[0],
      border: `1px solid ${BLACK[30]}`,
      borderRadius: '999px',
      color: BLACK[80],
      fontFamily: PRIMARY_FONT[700],
      fontSize: '12px',
      padding: '2px 10px',
    }}
  >
    {LanguageConverterV2('positionTemplate.create.shifts.count', { count })}
  </Box>
);

ShiftCountChip.propTypes = { count: PropTypes.number.isRequired };

const ShiftErrorMessage = ({ messageKey }) => (
  <Typography
    role="alert"
    sx={{ color: RED[80], fontFamily: PRIMARY_FONT[400], fontSize: '12px', gridColumn: '1 / -1' }}
  >
    {LanguageConverterV2(messageKey)}
  </Typography>
);

ShiftErrorMessage.propTypes = { messageKey: PropTypes.string.isRequired };

const ShiftRow = ({ shift, errors, showErrors, onUpdate, onDelete, onTouch }) => {
  const namePlaceholder = LanguageConverterV2('positionTemplate.create.shifts.namePlaceholder');
  const nameLabel = LanguageConverterV2('positionTemplate.create.shifts.column.name');
  const startLabel = LanguageConverterV2('positionTemplate.create.shifts.column.start');
  const endLabel = LanguageConverterV2('positionTemplate.create.shifts.column.end');
  const deleteLabel = LanguageConverterV2('positionTemplate.create.shifts.delete');

  const nameError = showErrors ? errors.name : null;
  const endError = showErrors ? errors.end : null;

  return (
    <Box
      data-testid="shift-row"
      sx={{
        alignItems: 'center',
        borderTop: `1px solid ${BLACK[20]}`,
        columnGap: '16px',
        display: 'grid',
        gridTemplateColumns: GRID_COLUMNS,
        padding: '12px 16px',
        rowGap: '6px',
      }}
    >
      <WaeStandardTextField
        value={shift.name}
        placeholder={namePlaceholder}
        onChange={(event) => onUpdate({ name: event.target.value })}
        onBlur={onTouch}
        error={!!nameError}
        inputProps={{ maxLength: SHIFT_NAME_MAX_LENGTH, 'aria-label': nameLabel }}
        sx={{ ...nameFieldSx, ...(nameError ? nameErrorSx : {}) }}
      />
      <TimeField
        value={shift.start}
        onChange={(start) => onUpdate({ start })}
        onBlur={onTouch}
        ariaLabel={startLabel}
      />
      <TimeField
        value={shift.end}
        onChange={(end) => onUpdate({ end })}
        onBlur={onTouch}
        error={!!endError}
        ariaLabel={endLabel}
      />
      <IconButton
        aria-label={deleteLabel}
        onClick={onDelete}
        size="small"
        sx={{ color: BLACK[60], '&:hover': { color: RED[50] } }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
      {nameError && <ShiftErrorMessage messageKey={nameError} />}
      {endError && <ShiftErrorMessage messageKey={endError} />}
    </Box>
  );
};

ShiftRow.propTypes = {
  shift: shiftShape.isRequired,
  errors: PropTypes.shape({ name: PropTypes.string, end: PropTypes.string }).isRequired,
  showErrors: PropTypes.bool,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onTouch: PropTypes.func.isRequired,
};

const ColumnHeaders = () => {
  const nameLabel = LanguageConverterV2('positionTemplate.create.shifts.column.name');
  const startLabel = LanguageConverterV2('positionTemplate.create.shifts.column.start');
  const endLabel = LanguageConverterV2('positionTemplate.create.shifts.column.end');

  return (
    <Box
      sx={{
        backgroundColor: BLACK[10],
        columnGap: '16px',
        display: 'grid',
        gridTemplateColumns: GRID_COLUMNS,
        padding: '10px 16px',
      }}
    >
      <Typography sx={columnLabelSx}>{nameLabel}</Typography>
      <Typography sx={columnLabelSx}>{startLabel}</Typography>
      <Typography sx={columnLabelSx}>{endLabel}</Typography>
      <Box />
    </Box>
  );
};

const ShiftsSection = ({ values, setField }) => {
  const title = LanguageConverterV2('positionTemplate.create.shifts.title');
  const addLabel = LanguageConverterV2('positionTemplate.create.shifts.add');
  const emptyText = LanguageConverterV2('positionTemplate.create.shifts.empty');
  const [touchedIds, setTouchedIds] = useState(() => new Set());

  const shifts = values.shifts || [];

  const addShift = () => setField('shifts', [...shifts, createShift()]);

  const updateShift = (id, patch) =>
    setField(
      'shifts',
      shifts.map((shift) => (shift.id === id ? { ...shift, ...patch } : shift))
    );

  const deleteShift = (id) =>
    setField(
      'shifts',
      shifts.filter((shift) => shift.id !== id)
    );

  const touchShift = (id) => setTouchedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));

  return (
    <Box
      id={getSectionAnchorId(SECTION_IDS.SHIFTS)}
      data-testid="shifts-section"
      sx={{ scrollMarginTop: '16px' }}
    >
      <SectionCard
        header={
          <>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: '10px' }}>
              <Typography
                sx={{ color: BLACK[100], fontFamily: PRIMARY_FONT[700], fontSize: '14px' }}
              >
                {title}
                <RequiredMark />
              </Typography>
              {shifts.length > 0 && <ShiftCountChip count={shifts.length} />}
            </Box>
            <WaeButton
              dataTestId="add-shift"
              onClick={addShift}
              startIcon={<AddIcon sx={{ color: PRIMARY_PURPLE[70] }} />}
              text={addLabel}
              variant={BUTTON_VARIANT.OUTLINED}
            />
          </>
        }
      >
        <Box sx={{ margin: '-8px -16px' }}>
          <ColumnHeaders />
          {shifts.length === 0 ? (
            <Typography
              sx={{
                borderTop: `1px solid ${BLACK[20]}`,
                color: BLACK[60],
                fontFamily: PRIMARY_FONT[400],
                fontSize: '13px',
                padding: '20px 16px',
                textAlign: 'center',
              }}
            >
              {emptyText}
            </Typography>
          ) : (
            shifts.map((shift) => (
              <ShiftRow
                key={shift.id}
                shift={shift}
                errors={getShiftErrors(shift, shifts)}
                showErrors={touchedIds.has(shift.id)}
                onUpdate={(patch) => updateShift(shift.id, patch)}
                onDelete={() => deleteShift(shift.id)}
                onTouch={() => touchShift(shift.id)}
              />
            ))
          )}
        </Box>
      </SectionCard>
    </Box>
  );
};

ShiftsSection.propTypes = {
  values: PropTypes.shape({
    shifts: PropTypes.arrayOf(shiftShape),
  }).isRequired,
  setField: PropTypes.func.isRequired,
};

export default ShiftsSection;
